import { ChatAttachment, ChatAttachmentKind } from 'src/types';

// ----------------------------------------------------------------------
// Broad attachment families. Previews (chat list rows) only need the family,
// not the exact format — `src/components/file-thumbnail` covers per-format
// icons where the real file is rendered.
// ----------------------------------------------------------------------

export const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];
export const HEIC_EXTENSIONS = ['heic', 'heif'];
export const VIDEO_EXTENSIONS = ['mp4', 'mov', 'webm', 'avi', 'mkv', 'm4v', 'mpg'];
export const AUDIO_EXTENSIONS = ['mp3', 'wav', 'ogg', 'oga', 'm4a', 'aac', 'flac', 'aif'];

export const PREVIEWABLE_EXTENSIONS = [
  ...IMAGE_EXTENSIONS,
  ...HEIC_EXTENSIONS,
  ...VIDEO_EXTENSIONS,
  ...AUDIO_EXTENSIONS,
  'pdf',
];

const extensionOf = (filename: string) => filename.split('.').pop()?.toLowerCase() ?? '';

export const isImageAttachment = (filename: string) =>
  IMAGE_EXTENSIONS.includes(extensionOf(filename));

export const isPreviewableAttachment = (filename: string) =>
  PREVIEWABLE_EXTENSIONS.includes(extensionOf(filename));

// The mime type wins when the backend sends one; the extension is the fallback
// for uploads that arrive without it.
export const getAttachmentKind = ({ filename, mime_type }: ChatAttachment): ChatAttachmentKind => {
  const family = mime_type?.split('/')[0];
  if (family === 'image' || family === 'video' || family === 'audio') return family;

  const extension = extensionOf(filename);
  if (IMAGE_EXTENSIONS.includes(extension) || HEIC_EXTENSIONS.includes(extension)) return 'image';
  if (VIDEO_EXTENSIONS.includes(extension)) return 'video';
  if (AUDIO_EXTENSIONS.includes(extension)) return 'audio';
  return 'file';
};

// A preview shows one icon, so a mixed batch is represented by its first file.
export const getPreviewAttachmentKind = (attachments: ChatAttachment[]) =>
  attachments.length ? getAttachmentKind(attachments[0]) : null;
